import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Services.css';

const Services = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const services = [
    {
      icon: 'fas fa-globe',
      title: 'Web Development',
      description: 'Business websites that present your company clearly — services, projects, team, and contact — with bilingual support when you need it. A live example is the Groupe DNK engineering site.',
      features: ['Business Websites', 'Bilingual EN/FR', 'Contact Forms', 'Maintenance & Hosting'],
      link: { label: 'See groupednk.ca', url: 'https://www.groupednk.ca/' },
      id: 'web-development'
    },
    {
      icon: 'fas fa-mobile-alt',
      title: 'Mobile App Development',
      description: 'Native iOS and Android development for businesses — including four published App Store titles. We handle new apps, Android-to-iOS conversions, and production fixes for apps already in the stores.',
      features: ['4 Apps in App Store', 'iOS & Android Native', 'Production Support', 'Full Lifecycle Delivery']
    },
    {
      icon: 'fas fa-cogs',
      title: 'Legacy System Modernization',
      description: 'Transform outdated systems into modern, efficient solutions using .NET and SQL Server. Proven track record in financial platform conversions.',
      features: ['.NET Migration', 'SQL Server Integration', 'Financial Systems', 'Legacy Conversion']
    },
    {
      icon: 'fas fa-users',
      title: 'Project Delivery & System Architecture',
      description: 'End-to-end delivery for small projects and larger implementations: architecture, team coordination, and full SDLC. We scope the work clearly so you know what ships and when.',
      features: ['Technical Leadership', 'System Architecture', 'Full SDLC', 'Clear Project Scope']
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <section id="services" className="services" ref={ref}>
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2>Our Services</h2>
          <p>Websites, mobile apps, and software solutions tailored to your business</p>
        </motion.div>

        <motion.div 
          className="services-grid"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              id={service.id}
              className="service-card"
              variants={cardVariants}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.3 }
              }}
            >
              <motion.div 
                className="service-icon"
                whileHover={{ 
                  scale: 1.1,
                  rotate: 5,
                  transition: { duration: 0.3 }
                }}
              >
                <i className={service.icon}></i>
              </motion.div>
              
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              
              <ul className="service-features">
                {service.features.map((feature, featureIndex) => (
                  <motion.li 
                    key={featureIndex}
                    initial={{ opacity: 0, x: -20 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.5 + featureIndex * 0.1 }}
                  >
                    {feature}
                  </motion.li>
                ))}
              </ul>
              {service.link && (
                <a
                  href={service.link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="service-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  {service.link.label}
                  <i className="fas fa-external-link-alt"></i>
                </a>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;


